import React from 'react';
import { Users, BookOpen, Target, Shield, Eye, Send, Calendar, CheckCircle, AlertTriangle } from 'lucide-react';
import useMyTeamStore from '@/stores/AdminStore/MyTeamStore';
import './TeamDashboardTab.css';

interface TeamDashboardTabProps {
  onViewUser: (userId: string) => void;
}

const TeamDashboardTab: React.FC<TeamDashboardTabProps> = ({ onViewUser }) => {
  const { teamMembers, teamStats } = useMyTeamStore();

  return (
    <div className="trainer-my-team-dashboard">
      <div className="trainer-my-team-stats">
        <div className="trainer-my-team-stat-card">
          <div className="trainer-my-team-stat-content">
            <div>
              <div className="trainer-my-team-stat-label">Team Members</div>
              <div className="trainer-my-team-stat-value">{teamStats?.totalMembers || 0}</div>
              <div className="trainer-my-team-stat-status trainer-my-team-status-active">{teamStats?.activeMembers || 'Loading...'}</div>
            </div>
            <div className="trainer-my-team-stat-icon trainer-my-team-icon-blue">
              <Users size={28} />
            </div>
          </div>
        </div>

        <div className="trainer-my-team-stat-card">
          <div className="trainer-my-team-stat-content">
            <div>
              <div className="trainer-my-team-stat-label">Active Learners</div>
              <div className="trainer-my-team-stat-value">{teamStats?.activeLearners || 0}</div>
              <div className="trainer-my-team-stat-status trainer-my-team-status-learning">{teamStats?.activeLearnersStatus || 'Loading...'}</div>
            </div>
            <div className="trainer-my-team-stat-icon trainer-my-team-icon-orange">
              <BookOpen size={28} />
            </div>
          </div>
        </div>

        <div className="trainer-my-team-stat-card">
          <div className="trainer-my-team-stat-content">
            <div>
              <div className="trainer-my-team-stat-label">Completion Rate</div>
              <div className="trainer-my-team-stat-value">{teamStats?.completionRate || '0%'}</div>
              <div className="trainer-my-team-stat-status trainer-my-team-status-increase">{teamStats?.completionRateChange || 'Loading...'}</div>
            </div>
            <div className="trainer-my-team-stat-icon trainer-my-team-icon-green">
              <Target size={28} />
            </div>
          </div>
        </div>

        <div className="trainer-my-team-stat-card">
          <div className="trainer-my-team-stat-content">
            <div>
              <div className="trainer-my-team-stat-label">Compliance %</div>
              <div className="trainer-my-team-stat-value">{teamStats?.complianceRate || '0%'}</div>
              <div className="trainer-my-team-stat-status trainer-my-team-status-overdue">{teamStats?.complianceIssue || 'Loading...'}</div>
            </div>
            <div className="trainer-my-team-stat-icon trainer-my-team-icon-purple">
              <Shield size={28} />
            </div>
          </div>
        </div>
      </div>

      <div className="trainer-my-team-section">
        <h2 className="trainer-my-team-section-title">Team Members</h2>
        <div className="trainer-my-team-table-container">
          <table className="trainer-my-team-table">
            <thead>
              <tr>
                <th>NAME</th>
                <th>JOB TITLE</th>
                <th>PROGRESS</th>
                <th>REQUIRED TRAINING</th>
                <th>NEXT DEADLINE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {teamMembers?.map(member => (
                <tr key={member.id}>
                  <td>
                    <div className="trainer-my-team-member-info">
                      <div className="trainer-my-team-avatar">{member.initials}</div>
                      <div>
                        <div className="trainer-my-team-member-name">{member.name}</div>
                        <div className="trainer-my-team-member-email">{member.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="trainer-my-team-job-title">{member.jobTitle}</div>
                    <div className="trainer-my-team-department">{member.department}</div>
                  </td>
                  <td>
                    <div className="trainer-my-team-progress-label">Completion {member.progress}%</div>
                    <div className="trainer-my-team-progress-bar">
                      <div
                        className="trainer-my-team-progress-fill"
                        style={{ width: `${member.progress}%` }}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="trainer-my-team-training-info">
                      <span className="trainer-my-team-training-badge">
                        <CheckCircle size={14} /> {member.requiredTraining}
                      </span>
                      {member.overdue > 0 && (
                        <span className="trainer-my-team-overdue-badge">
                          <AlertTriangle size={14} /> {member.overdue} overdue
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="trainer-my-team-deadline">
                      <Calendar size={14} /> {member.nextDeadline}
                    </div>
                  </td>
                  <td>
                    <div className="trainer-my-team-actions">
                      <button
                        className="trainer-my-team-action-btn"
                        onClick={() => onViewUser(member.id)}
                        title="View user details"
                      >
                        <Eye size={16} />
                      </button>
                      <button className="trainer-my-team-action-btn" title="Send message">
                        <Send size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TeamDashboardTab;
